<template>
  <div class="relative select-none">
    <div
      class="h-16 px-6 sticky top-18 z-20 bg-slate-300 border-b border-slate-400 flex flex-grow items-center justify-between gap-4"
      :style="{ minWidth: 'calc(100vw - 22rem)' }"
    >
      <div></div>
      <div class="sticky top-18 right-6 flex items-center gap-2">
        <router-link to="client-acquisition/edit">
          <BlInputButton type="outlined" label="Edit pipeline" />
        </router-link>
      </div>
    </div>
    <div v-if="previewLoading" class="w-full h-92 grid place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else class="px-6 pt-3 pb-1 flex gap-6">
      <PipelineStage
        v-for="stage in stages"
        :key="stage.id"
        :stage="stage"
        :dragId="dragId"
        @drag-start="dragStart"
      />
    </div>
    <div class="fixed bottom-0 right-0 z-40 p-6">
      <button
        class="h-25 w-25 flex flex-col items-center justify-center gap-1 bg-light-purple hover:bg-dark-purple-200 rounded-full border-2 border-pure-white shadow-lg outline-none"
        title="New Target Databank"
        @click.prevent="newProspectOpened = true"
      >
        <BlIcon icon="plus" color="secondary" />
        <BlTextSubtitle3 class="text-pure-white" text="New" />
      </button>
    </div>
    <NewProspectModal
      v-if="newProspectOpened"
      @close-modal="newProspectOpened = false"
      @after-commit="fetchStageProspectStages($event)"
    />
    <MoveProspectModal
      v-if="moveProspectOpened && destinationStage"
      :prospectStage="prospectStage"
      :destinationStage="destinationStage"
      location="kanban"
      @close-modal="moveProspectOpened = false"
    />
    <BlBackdrop v-if="prospectMoveToOpened" :footer="false">
      <div class="px-8 pt-8 pb-4 flex flex-col gap-2">
        <BlTextTitle4 text="Move prospect to" />
        <div class="flex">
          <div class="h-7 px-1 flex items-center gap-1 rounded bg-tulip-100">
            <BlIcon icon="user-square" color="tulip-7" class="h-6 w-6" />
            <BlTextSubtitle3
              :text="prospectStage.prospect.name"
              class="text-tulip-700"
            />
          </div>
        </div>
      </div>
      <div
        class="px-8 flex flex-col gap-4 overflow-y-auto"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 16rem)' }"
      >
        <div
          v-for="stage in stages"
          :key="stage.id"
          class="flex items-center gap-4 cursor-pointer"
          :class="{
            'opacity-50 pointer-events-none':
              stage.id === prospectStage.stage.id
          }"
          @click="destinationStageId = stage.id"
        >
          <BlInputRadio
            :checked="stage.id === destinationStageId"
            :disabled="stage.id === prospectStage.stage.id"
            name="select_destination_stage"
          />
          <BlChip :chip="stage.name" />
        </div>
      </div>
      <div class="px-8 pt-4 pb-8 flex items-center justify-end gap-4">
        <BlInputButton
          type="plain"
          label="Cancel"
          @clicked="prospectMoveToOpened = false"
        />
        <BlInputButton
          label="Confirm"
          :disabled="!destinationStageId"
          :loading="moveProspectLoading"
          @clicked="onMoveProspect"
        />
      </div>
    </BlBackdrop>
    <!--  -->
    <div
      v-if="dragTarget"
      ref="blProspectStageDecoy"
      :style="{
        left: `${dragPos.x}px`,
        top: `${dragPos.y}px`,
        width: `${dragPos.w}px`,
        zIndex: 100
      }"
      class="pointer-events-none fixed"
    >
      <PipelineProspectStage :key="dragTarget.id" :item="dragTarget" />
    </div>
    <div
      v-if="dragMove"
      class="fixed bottom-0 right-0 z-50 p-3 grid grid-cols-3 items-center gap-3 bg-pure-white border border-slate-400"
      :style="{ minWidth: 'calc(100vw - 22rem)' }"
    >
      <div
        class="h-24 flex items-center justify-center flex-shrink-0 border-2 border-cornflower-800 bg-cornflower-200 border-dashed hover:bg-cornflower-800 hover:border-solid"
        @mouseup="openMoveProspect"
      >
        <BlTextSubtitle2
          text="Move to"
          class="h-24 w-full text-center flex items-center justify-center text-cornflower-800 hover:text-pure-white"
        />
      </div>
      <div
        class="h-24 flex items-center justify-center flex-shrink-0 border-2 border-rose-700 bg-rose-200 border-dashed hover:bg-rose-700 hover:border-solid"
        @mouseup="openMoveProspectToDiscontinued"
      >
        <BlTextSubtitle2
          text="Discontinued"
          class="h-24 w-full text-center flex items-center justify-center text-rose-700 hover:text-pure-white"
        />
      </div>
      <div
        class="h-24 flex items-center justify-center flex-shrink-0 border-2 border-lime-700 bg-lime-200 border-dashed hover:bg-lime-700 hover:border-solid"
        @mouseup="openMoveProspectToAdmitted"
      >
        <BlTextSubtitle2
          text="Admitted"
          class="h-12 w-full text-center flex items-center justify-center text-lime-700 hover:text-pure-white"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import PipelineStage from "./Stage.vue";
import NewProspectModal from "../modal/NewProspectModal.vue";
import MoveProspectModal from "../modal/MoveProspectModal.vue";
import PipelineProspectStage from "./ProspectStage.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "PipelineKanban",
  components: {
    PipelineStage,
    NewProspectModal,
    MoveProspectModal,
    PipelineProspectStage,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlIcon,
    BlChip,
    BlInputButton,
    BlInputRadio,
    BlBackdrop
  },
  data() {
    return {
      previewLoading: false,
      newProspectOpened: false,
      //
      prospectMoveToOpened: false,
      moveProspectOpened: false,
      moveProspectLoading: false,
      prospectStage: null,
      destinationStageId: 0,
      //
      dragX: 0,
      dragY: 0,
      dragPos: { x: 0, y: 0, w: 0 },
      dragMove: false,
      dragId: 0,
      dragTarget: null
    };
  },
  computed: {
    ...mapGetters(["currentUser", "stages"]),
    destinationStage() {
      if (!this.destinationStageId) return {};
      return this.stages.find(i => i.id === this.destinationStageId) || {};
    }
  },
  mounted() {
    this.fetchStages();
  },
  methods: {
    ...mapActions(["getStages", "getStageProspectStages", "moveProspect"]),
    fetchStages() {
      if (this.$route.path !== "/client-acquisition") return;

      this.previewLoading = true;
      this.getStages().then(async () => {
        this.previewLoading = false;
      });

      // sync url path for page
      const incomingPath = `/client-acquisition?preview_type=kanban`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    fetchStageProspectStages(stageId) {
      this.getStageProspectStages({ id: stageId });
    },
    //
    openMoveProspect() {
      this.prospectStage = this.dragTarget;
      this.destinationStageId = 0;
      this.prospectMoveToOpened = true;
    },
    openMoveProspectToAdmitted() {
      const admittedStage = this.stages.find(i => i.is_admitted);
      if (admittedStage) {
        this.prospectStage = this.dragTarget;
        this.destinationStageId = admittedStage.id;
        this.moveProspectOpened = true;
      }
    },
    openMoveProspectToDiscontinued() {
      const discontinuedStage = this.stages.find(i => i.is_discontinued);
      if (discontinuedStage) {
        this.prospectStage = this.dragTarget;
        this.destinationStageId = discontinuedStage.id;
        this.moveProspectOpened = true;
      }
    },
    onMoveProspect() {
      if (
        this.destinationStage.is_admitted ||
        this.destinationStage.is_discontinued
      ) {
        this.prospectMoveToOpened = false;
        this.moveProspectOpened = true;
      } else {
        const data = {
          id: this.prospectStage.prospect.id,
          prospect_name: this.prospectStage.prospect.name,
          stage_id: this.destinationStageId,
          stage_name: this.destinationStage.name
        };
        this.moveProspectLoading = true;
        this.moveProspect(data).then(async ({ success }) => {
          if (!success) {
            this.moveProspectLoading = false;
            return;
          }
          this.getStageProspectStages({ id: this.prospectStage.stage.id });
          this.getStageProspectStages({ id: this.destinationStage.id });
          this.moveProspectLoading = false;
          this.prospectMoveToOpened = false;
        });
      }
    },
    //
    dragStart(payload) {
      this.prospectStage = null;
      this.destinationStageId = 0;
      //
      const e = payload.e;
      const prospectStage = payload.prospect_stage;
      const target = e.target ? e.target.parentElement : null;
      if (prospectStage.is_admitted) return;
      //
      if (target && prospectStage) {
        if (e.type === "mousedown") {
          const { x, y, width } = target.getBoundingClientRect();

          this.dragX = e.clientX;
          this.dragY = e.clientY;
          this.dragId = prospectStage.id;
          this.dragPos = { x, y, w: width };

          this.dragTarget = prospectStage;
          window.addEventListener("mousemove", this.drag);
          window.addEventListener("mouseup", this.dragEnd);
        } else {
          window.addEventListener("touchmove", this.drag);
          window.addEventListener("touchend", this.dragEnd);
        }
      }
    },
    drag(e) {
      this.dragMove = true;
      let dx = 0;
      let dy = 0;

      if (e.type === "mousemove") {
        dx = e.clientX - this.dragX;
        dy = e.clientY - this.dragY;
      }
      if (this.$refs.blProspectStageDecoy) {
        this.$refs.blProspectStageDecoy.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      }
    },
    dragEnd() {
      setTimeout(() => {
        this.dragPos = { x: 0, y: 0, w: 0 };
        this.dragId = 0;
        this.dragTarget = null;
        this.dragMove = false;
        window.removeEventListener("mousemove", this.drag);
        window.removeEventListener("mouseup", this.dragEnd);
        window.removeEventListener("touchmove", this.drag);
        window.removeEventListener("touchend", this.dragEnd);
      }, 10);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/pipeline/Kanban.vue